import { Button, Menu } from '@mantine/core'
import type { Patient } from '@medplum/fhirtypes'
import {
  IconAlertTriangle,
  IconChevronDown,
  IconCloudDownload,
  IconHistory,
  IconRefresh,
} from '@tabler/icons-react'
import { useState } from 'react'
import { formatDateTime } from '@/utils.ts'
import { TefcaMatchReviewModal } from './TefcaMatchReviewModal'
import { useTefcaOpenReview } from './useTefcaOpenReview'

export type HgSyncStatus = 'idle' | 'in-progress' | 'completed' | 'failed'

type Props = {
  patient: Patient
  syncStatus: HgSyncStatus
  syncUpdatedAt: string | null
  isImporting: boolean
  isProjectAdmin: boolean
  onImport: (fetchEverything?: boolean) => void
  onOpenHistory: () => void
}

const DEFAULT_FACE_CLASSNAME =
  '!text-primary !border-[#D0E1F2] !bg-gradient-to-b from-[#F0F7FF] to-[#FFFFFF] whitespace-nowrap hover:from-[#FFFFFF]'

/**
 * Single TEFCA control on the patient chart: the face reflects the most
 * urgent state (open Match Review > sync in progress > last sync result) and
 * clicking it does the state's primary action (open the review, or start an
 * import). The caret menu carries every action: import, sync history, review
 * match (while a review is open), and the admin-only re-import from scratch.
 */
export const TefcaSyncControl = ({
  patient,
  syncStatus,
  syncUpdatedAt,
  isImporting,
  isProjectAdmin,
  onImport,
  onOpenHistory,
}: Props) => {
  const [isReviewOpen, setIsReviewOpen] = useState(false)
  const openReview = useTefcaOpenReview(patient.id as string)

  const importDisabled = syncStatus === 'in-progress' || isImporting
  const updatedAt = syncUpdatedAt ? formatDateTime(syncUpdatedAt, '/', false, true) : null

  let faceLabel = 'Import from TEFCA'
  let faceIcon = <IconCloudDownload size={16} />
  let faceDateLine: string | null = null
  let faceColor: string | undefined
  let faceDisabled = false
  let faceOnClick = () => onImport()

  if (openReview) {
    faceLabel = 'Needs TEFCA Review'
    faceIcon = <IconAlertTriangle size={16} />
    faceColor = 'orange'
    faceOnClick = () => setIsReviewOpen(true)
  } else {
    switch (syncStatus) {
      case 'in-progress':
        faceLabel = 'TEFCA Sync In Progress…'
        faceIcon = <IconRefresh size={16} className="animate-spin" />
        faceDateLine = updatedAt
        faceColor = 'yellow'
        faceDisabled = true
        break
      case 'failed':
        faceDateLine = updatedAt ? `Sync Failed · ${updatedAt}` : 'Sync Failed'
        faceColor = 'red'
        break
      case 'completed':
        faceDateLine = updatedAt ? `Synced · ${updatedAt}` : 'Synced'
        faceColor = 'green'
        break
    }
    faceDisabled = faceDisabled || importDisabled
  }

  const buttonProps = faceColor ? { color: faceColor } : { className: DEFAULT_FACE_CLASSNAME }

  return (
    <>
      <Button.Group className="shrink-0">
        <Button
          {...buttonProps}
          leftSection={faceIcon}
          disabled={faceDisabled}
          onClick={faceOnClick}
        >
          <span className="flex flex-col items-start leading-tight">
            <span>{faceLabel}</span>
            {faceDateLine && (
              <span className="text-[10px] font-normal opacity-80">{faceDateLine}</span>
            )}
          </span>
        </Button>
        <Menu zIndex={1000} position="bottom-end" shadow="md">
          <Menu.Target>
            <Button disabled={faceDisabled} {...buttonProps} px={6} aria-label="TEFCA actions">
              <IconChevronDown size={16} />
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconRefresh size={16} />}
              disabled={importDisabled}
              onClick={() => onImport()}
            >
              Import now
            </Menu.Item>
            <Menu.Item leftSection={<IconHistory size={16} />} onClick={onOpenHistory}>
              Sync history
            </Menu.Item>
            {openReview && (
              <Menu.Item
                color="orange"
                leftSection={<IconAlertTriangle size={16} />}
                onClick={() => setIsReviewOpen(true)}
              >
                Review match
              </Menu.Item>
            )}
            {isProjectAdmin && (
              <>
                <Menu.Divider />
                <Menu.Item
                  color="red"
                  leftSection={<IconAlertTriangle size={16} />}
                  disabled={isImporting}
                  onClick={() => onImport(true)}
                >
                  Re-import from scratch
                </Menu.Item>
              </>
            )}
          </Menu.Dropdown>
        </Menu>
      </Button.Group>
      {isReviewOpen && openReview && (
        <TefcaMatchReviewModal
          patient={patient}
          review={openReview}
          opened
          onClose={() => setIsReviewOpen(false)}
        />
      )}
    </>
  )
}
